/**
 * MapGenerator - Generates roguelike map with upward-only connections
 */

import { MapGraph, MapNode, NodeType } from '../types';

export class MapGenerator {
  private static readonly BOARD_WIDTH = 8; // 8 columns (a-h)

  /**
   * Generate a new map for a run
   * @param totalLevels Number of levels (floors) in the map
   * @param seed Random seed for reproducibility
   */
  static generateMap(totalLevels: number = 12, seed?: string): MapGraph {
    const nodes = new Map<string, MapNode>();
    const seededRandom = seed ? this.createSeededRandom(seed) : Math.random;

    // Generate ranks to support all levels (1 through totalLevels)
    // Rank 0: starting nodes (level 1), last rank: boss (level totalLevels)
    // Intermediate ranks spaced evenly for the remaining levels
    const ranks: number[] = [];
    for (let i = 0; i < totalLevels; i++) {
      ranks.push(i * 2);
    }

    // Create 2-3 starting nodes at rank 0 (all available for player to choose)
    const numStartNodes = 2 + Math.floor(seededRandom() * 2); // 2 or 3
    const startingNodes: MapNode[] = [];
    const usedColumns = new Set<number>();

    for (let i = 0; i < numStartNodes; i++) {
      let col: number;
      let attempts = 0;

      // Find a column that's not already used and has spacing
      do {
        col = Math.floor(seededRandom() * this.BOARD_WIDTH);
        attempts++;
      } while (usedColumns.has(col) && attempts < 1000);

      // Ensure spacing between start nodes (at least 2 columns apart)
      if (Array.from(usedColumns).some(usedCol => Math.abs(usedCol - col) < 2)) {
        let found = false;
        for (let testCol = 0; testCol < this.BOARD_WIDTH; testCol++) {
          if (!usedColumns.has(testCol) &&
              !Array.from(usedColumns).some(usedCol => Math.abs(usedCol - testCol) < 2)) {
            col = testCol;
            found = true;
            break;
          }
        }
        if (!found) {
          col = Math.floor(seededRandom() * this.BOARD_WIDTH);
        }
      }

      usedColumns.add(col);

      // All level 1 nodes should be simple enemy encounters
      const startNode: MapNode = {
        id: `start_${col}_${i}`,
        type: 'enemy',
        level: 1,
        col,
        row: 0,
        connections: [],
        visited: false, // Will be marked as available in MapScene
      };
      nodes.set(startNode.id, startNode);
      startingNodes.push(startNode);
    }

    let previousRankNodes: MapNode[] = startingNodes;

    // Generate intermediate ranks (2-12)
    for (let rankIndex = 1; rankIndex < ranks.length - 1; rankIndex++) {
      const row = ranks[rankIndex];
      const currentRankNodes: MapNode[] = [];

      // All nodes in this rank have the same level (based on depth from start)
      const level = rankIndex + 1; // Start is level 1, so rank index 1 = level 2, etc.

      // Create 1-3 nodes at this rank, with 2 being most common
      // 20% chance of 1 node, 60% chance of 2 nodes, 20% chance of 3 nodes
      let numNodes: number;
      const rand = seededRandom();
      if (rand < 0.2) {
        numNodes = 1;
      } else if (rand < 0.8) {
        numNodes = 2;
      } else {
        numNodes = 3;
      }

      const allExistingNodes = Array.from(nodes.values());
      const usedColumns = new Set<number>();

      for (let i = 0; i < numNodes; i++) {
        let col: number;
        let attempts = 0;

        // Find a column that's not adjacent to any existing nodes and not already used
        do {
          col = Math.floor(seededRandom() * this.BOARD_WIDTH);
          attempts++;
        } while ((usedColumns.has(col) || this.isAdjacentToAnyNode(col, row, allExistingNodes)) && attempts < 1000);

        // Fallback: try all columns
        if (usedColumns.has(col) || this.isAdjacentToAnyNode(col, row, allExistingNodes)) {
          let found = false;
          for (let testCol = 0; testCol < this.BOARD_WIDTH; testCol++) {
            if (!usedColumns.has(testCol) && !this.isAdjacentToAnyNode(testCol, row, allExistingNodes)) {
              col = testCol;
              found = true;
              break;
            }
          }
          if (!found) {
            // If we still can't find a spot, just use a random column
            col = Math.floor(seededRandom() * this.BOARD_WIDTH);
          }
        }

        usedColumns.add(col);

        const nodeType = this.determineNodeType(level, totalLevels, seededRandom);
        const node: MapNode = {
          id: `node_${level}_${col}_${row}_${i}`,
          type: nodeType,
          level,
          col,
          row,
          connections: [],
          visited: false,
        };

        nodes.set(node.id, node);
        currentRankNodes.push(node);
      }

      // Connect previous rank nodes to current rank nodes
      // Strategy: Create branching paths by connecting each previous node to 1-2 nearby nodes
      // Sort current nodes by column to create more spatial coherence
      const sortedCurrent = [...currentRankNodes].sort((a, b) => a.col - b.col);

      for (const prevNode of previousRankNodes) {
        // Find the 2-3 closest nodes in the current rank by column distance
        const nodesByDistance = sortedCurrent
          .map(node => ({
            node,
            distance: Math.abs(node.col - prevNode.col)
          }))
          .sort((a, b) => a.distance - b.distance);

        // Connect to 1-2 of the closest nodes
        const numConnections = 1 + Math.floor(seededRandom() * 2); // 1 or 2

        for (let i = 0; i < Math.min(numConnections, nodesByDistance.length); i++) {
          const targetNode = nodesByDistance[i].node;
          if (!prevNode.connections.includes(targetNode.id)) {
            prevNode.connections.push(targetNode.id);
          }
        }
      }

      // Ensure all current rank nodes are reachable (but don't add excessive connections)
      for (const currentNode of currentRankNodes) {
        const isConnected = previousRankNodes.some(prev =>
          prev.connections.includes(currentNode.id)
        );

        if (!isConnected && previousRankNodes.length > 0) {
          // Find the closest previous node that doesn't already connect to this node
          const prevByDistance = previousRankNodes
            .map(node => ({
              node,
              distance: Math.abs(node.col - currentNode.col)
            }))
            .sort((a, b) => a.distance - b.distance);

          // Connect the closest previous node
          if (prevByDistance.length > 0) {
            prevByDistance[0].node.connections.push(currentNode.id);
          }
        }
      }

      // Uncross edges by swapping destinations when edges would cross
      this.uncrossEdges(previousRankNodes, currentRankNodes);

      previousRankNodes = currentRankNodes;
    }

    // Create boss node at the final rank
    const bossRow = ranks[ranks.length - 1];
    const allExistingNodes = Array.from(nodes.values());
    let bossCol = Math.floor(seededRandom() * this.BOARD_WIDTH);
    let attempts = 0;

    do {
      bossCol = Math.floor(seededRandom() * this.BOARD_WIDTH);
      attempts++;
    } while (this.isAdjacentToAnyNode(bossCol, bossRow, allExistingNodes) && attempts < 1000);

    if (this.isAdjacentToAnyNode(bossCol, bossRow, allExistingNodes)) {
      for (let testCol = 0; testCol < this.BOARD_WIDTH; testCol++) {
        if (!this.isAdjacentToAnyNode(testCol, bossRow, allExistingNodes)) {
          bossCol = testCol;
          break;
        }
      }
    }

    const bossNode: MapNode = {
      id: `boss_${bossCol}_${bossRow}`,
      type: 'boss',
      level: totalLevels,
      col: bossCol,
      row: bossRow,
      connections: [],
      visited: false,
    };
    nodes.set(bossNode.id, bossNode);

    // Connect all nodes from last rank to boss
    for (const prevNode of previousRankNodes) {
      prevNode.connections.push(bossNode.id);
    }

    console.log('[MapGenerator] Generated branching map with ranks:', ranks);
    console.log('[MapGenerator] Total nodes:', nodes.size);
    console.log('[MapGenerator] Starting node IDs:', startingNodes.map(n => n.id).join(', '));

    return {
      nodes,
      totalLevels,
      startNodeIds: startingNodes.map(n => n.id),
    };
  }

  /**
   * Uncross edges by swapping destinations when two edges would cross
   */
  private static uncrossEdges(previousRankNodes: MapNode[], currentRankNodes: MapNode[]): void {
    // Build a list of all connections with source and destination columns
    const connections: Array<{ fromNode: MapNode; toNodeId: string; toCol: number }> = [];

    for (const prevNode of previousRankNodes) {
      for (const targetId of prevNode.connections) {
        const targetNode = currentRankNodes.find(n => n.id === targetId);
        if (targetNode) {
          connections.push({
            fromNode: prevNode,
            toNodeId: targetId,
            toCol: targetNode.col
          });
        }
      }
    }

    // Iteratively swap crossed edges until no more crossings exist
    let changed = true;
    let iterations = 0;
    const maxIterations = 100; // Prevent infinite loops

    while (changed && iterations < maxIterations) {
      changed = false;
      iterations++;

      for (let i = 0; i < connections.length; i++) {
        for (let j = i + 1; j < connections.length; j++) {
          const conn1 = connections[i];
          const conn2 = connections[j];

          // Skip if same source node
          if (conn1.fromNode.id === conn2.fromNode.id) continue;

          // Check if edges cross
          // Two edges cross if one goes left-to-right while the other goes right-to-left
          const crosses =
            (conn1.fromNode.col < conn2.fromNode.col && conn1.toCol > conn2.toCol) ||
            (conn1.fromNode.col > conn2.fromNode.col && conn1.toCol < conn2.toCol);

          if (crosses) {
            // Swap destinations
            const tempId = conn1.toNodeId;
            const tempCol = conn1.toCol;

            conn1.toNodeId = conn2.toNodeId;
            conn1.toCol = conn2.toCol;

            conn2.toNodeId = tempId;
            conn2.toCol = tempCol;

            changed = true;
          }
        }
      }
    }

    // Apply the uncrossed connections back to the nodes
    for (const prevNode of previousRankNodes) {
      prevNode.connections = [];
    }

    for (const conn of connections) {
      conn.fromNode.connections.push(conn.toNodeId);
    }
  }

  /**
   * Determine node type based on level and position in run
   */
  private static determineNodeType(level: number, totalLevels: number, random: () => number): NodeType {
    // Boss at final level
    if (level === totalLevels) {
      return 'boss';
    }

    // Elite encounters every 4 levels (higher chance)
    if (level % 4 === 0) {
      return random() < 0.6 ? 'elite' : 'enemy';
    }

    // Shop/heal/treasure/puzzle less frequently
    const rand = random();
    if (rand < 0.1) {
      return 'shop';
    } else if (rand < 0.18) {
      return 'heal';
    } else if (rand < 0.23) {
      return 'treasure';
    } else if (rand < 0.31) {
      return 'puzzle';
    } else {
      // Most common: regular enemy
      return 'enemy';
    }
  }

  /**
   * Create a seeded random function
   */
  private static createSeededRandom(seed: string): () => number {
    let hash = 0;
    for (let i = 0; i < seed.length; i++) {
      const char = seed.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash | 0; // Convert to 32-bit integer
    }

    return () => {
      hash = (hash * 9301 + 49297) % 233280;
      // Ensure hash is always positive (JavaScript modulo can return negative)
      if (hash < 0) {
        hash += 233280;
      }
      return hash / 233280;
    };
  }

  /**
   * Check if a position is adjacent (orthogonally or diagonally) to any existing nodes
   */
  private static isAdjacentToAnyNode(col: number, row: number, existingNodes: MapNode[]): boolean {
    for (const node of existingNodes) {
      const colDiff = Math.abs(node.col - col);
      const rowDiff = Math.abs(node.row - row);

      // Adjacent if within 1 column AND 1 row (includes orthogonal and diagonal)
      if (colDiff <= 1 && rowDiff <= 1 && !(colDiff === 0 && rowDiff === 0)) {
        return true;
      }
    }
    return false;
  }
}
